import { BRANDBOOK } from "@/lib/brand/brandbook";
import { BRAND_NAME } from "@/lib/brand";
import type { BrandLogoVariantId } from "@/lib/brand/logo-variant";
import { BITRIX_LAUNCHER_LEFT_MAX_WIDTH_PX } from "@/lib/bitrix24/site-button";

export type BitrixLivechatHeaderLogo = {
  alt: string;
  /** lockup = знак + «ИдеАлитика», mark = только знак */
  mode: "lockup" | "mark";
  markSrc: string;
  height: number;
};

const BITRIX_LIVECHAT_ICON_SRC = "/brand/logo/icon.svg";
const BITRIX_LIVECHAT_MARK_N_SRC = "/brand/logo-n/icon.svg";

/** Логотип для шапки livechat — компактный lockup как в шапке сайта. */
export function resolveBitrixLivechatHeaderLogo(
  variant: BrandLogoVariantId,
  _isDark: boolean,
): BitrixLivechatHeaderLogo {
  if (variant === "asset") {
    return {
      mode: "lockup",
      markSrc: BITRIX_LIVECHAT_ICON_SRC,
      alt: BRAND_NAME,
      height: 28,
    };
  }

  return {
    mode: "mark",
    markSrc: BITRIX_LIVECHAT_MARK_N_SRC,
    alt: BRAND_NAME,
    height: 28,
  };
}

export type BitrixLivechatPalette = {
  head: string;
  headFg: string;
  surface: string;
  surfaceMuted: string;
  text: string;
  textMuted: string;
  border: string;
  shadow: string;
  launcherBg: string;
  launcherShadow: string;
};

export const BITRIX_LIVECHAT_LAUNCHER_ICON = "/brand/bitrix-livechat-launcher.svg";

/** Белая стрелка «отправить» поверх фирменного фона кнопки. */
const BITRIX_SEND_ARROW_ICON = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M3 8h8M9 5l3 3-3 3' stroke='%23ffffff' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

function bitrixSendButtonBackground(color: string): string {
  return `${BITRIX_SEND_ARROW_ICON} center / 14px 14px no-repeat, ${color}`;
}

const BITRIX_RATE_LIKE_ICON_LIGHT = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 11v8M7 11l-3-1V8l3-1M7 11l4-6a2 2 0 012 2v4h6l-1 6a2 2 0 01-2 2H7' stroke='%2352525b' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
const BITRIX_RATE_DISLIKE_ICON_LIGHT = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M17 13V5M17 13l3 1v2l-3 1M17 13l-4 6a2 2 0 01-2-2v-4H5l1-6a2 2 0 012-2h9' stroke='%2352525b' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
const BITRIX_RATE_LIKE_ICON_DARK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 11v8M7 11l-3-1V8l3-1M7 11l4-6a2 2 0 012 2v4h6l-1 6a2 2 0 01-2 2H7' stroke='%23f5f5f7' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
const BITRIX_RATE_DISLIKE_ICON_DARK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M17 13V5M17 13l3 1v2l-3 1M17 13l-4 6a2 2 0 01-2-2v-4H5l1-6a2 2 0 012-2h9' stroke='%23f5f5f7' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
const BITRIX_RATE_LIKE_ICON_ACTIVE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M7 11v8M7 11l-3-1V8l3-1M7 11l4-6a2 2 0 012 2v4h6l-1 6a2 2 0 01-2 2H7' stroke='%23ffffff' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;

const BITRIX_HELLO_POPUP_ABOVE_LAUNCHER = "calc(3.5rem + 0.75rem)";

const BITRIX_HELLO_CLOSE_ICON_LIGHT = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4.25 4.25l7.5 7.5M11.75 4.25l-7.5 7.5' stroke='%2371717a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`;
const BITRIX_HELLO_CLOSE_ICON_DARK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none'%3E%3Cpath d='M4.25 4.25l7.5 7.5M11.75 4.25l-7.5 7.5' stroke='%23a1a1aa' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`;

type BitrixHelloPopupGlass = {
  background: string;
  border: string;
  shadow: string;
  blur: string;
};

function resolveBitrixHelloPopupGlass(isDark: boolean): BitrixHelloPopupGlass {
  if (isDark) {
    return {
      background: "rgba(22, 24, 31, 0.9)",
      border: "rgba(255, 255, 255, 0.08)",
      shadow: "0 8px 24px rgb(0 0 0 / 0.32)",
      blur: "blur(12px)",
    };
  }

  return {
    background: "rgba(255, 255, 255, 0.92)",
    border: "rgba(6, 8, 11, 0.08)",
    shadow: "0 8px 24px rgb(6 8 11 / 0.1)",
    blur: "blur(12px)",
  };
}

function isBitrixLikeControlButton(node: HTMLElement): boolean {
  const className = node.className?.toString?.() ?? "";
  return /like|vote|rating/i.test(className);
}

function isBitrixLikeControlActive(node: HTMLElement): boolean {
  const className = node.className?.toString?.() ?? "";
  return (
    node.classList.contains("bx-livechat-btn-like-active") ||
    /(?:^|\s)(?:active|selected)(?:\s|$)/i.test(className)
  );
}

/** Открыт ли livechat — для паузы idle-анимации лаунчера. */
export function isBitrixLivechatOpen(root: ParentNode = document): boolean {
  const livechat = root.querySelector(".bx-livechat-wrapper");
  if (livechat?.classList.contains("bx-livechat-show")) return true;

  const launcher = root.querySelector(".b24-widget-button-wrapper");
  if (
    launcher?.classList.contains("b24-widget-button-active") ||
    launcher?.classList.contains("b24-widget-button-hide")
  ) {
    return true;
  }

  const closeIcon = root.querySelector(".b24-widget-button-close");
  if (
    closeIcon instanceof HTMLElement &&
    getComputedStyle(closeIcon).display !== "none"
  ) {
    return true;
  }

  return false;
}

export function syncBitrixLauncherIdleState(root: ParentNode = document): void {
  if (typeof document === "undefined") return;
  const open = isBitrixLivechatOpen(root);
  document.documentElement.dataset.ixBxChatOpen = open ? "true" : "false";

  if (!open) {
    root.querySelectorAll(".bx-livechat-wrapper").forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;
      if (!livechatHasMessages(wrapper)) {
        delete wrapper.dataset.ixBxChatStarted;
      }
    });
  }

  root.querySelectorAll(".b24-widget-button-inner-container").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.classList.toggle("ix-bx-launcher-idle", !open);
  });
}

/** Поднимает лаунчер над баннером cookies; hello-popup скрывается, пока баннер виден. */
export function syncBitrixCookieBannerLauncherOffset(
  root: ParentNode = document,
): void {
  if (typeof document === "undefined") return;

  const hasBanner = document.documentElement.dataset.ixCookieBanner === "visible";
  const offset =
    document.documentElement.style
      .getPropertyValue("--ix-cookie-banner-offset")
      .trim() || "0px";
  const bottom = hasBanner ? `calc(1.5rem + ${offset})` : "";

  root
    .querySelectorAll(".b24-widget-button-wrapper, .bx-livechat-wrapper")
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const cornerLeft =
        document.documentElement.dataset.ixBxLauncherCorner === "left" &&
        typeof window !== "undefined" &&
        window.matchMedia(
          `(max-width: ${BITRIX_LAUNCHER_LEFT_MAX_WIDTH_PX}px)`,
        ).matches;
      if (cornerLeft) {
        setImportant(node, "right", "auto");
        setImportant(node, "left", "1.5rem");
      } else {
        node.style.removeProperty("left");
        node.style.removeProperty("right");
        // Bitrix снова выставит right сам; если нет — дефолт справа
      }
      if (hasBanner) {
        setImportant(node, "bottom", bottom);
        setImportant(
          node,
          "transition",
          "bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        );
      } else {
        node.style.removeProperty("bottom");
        node.style.removeProperty("transition");
      }
    });

  root.querySelectorAll(".b24-widget-button-popup").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (hasBanner) {
      setImportant(node, "display", "none");
      setImportant(node, "visibility", "hidden");
      setImportant(node, "pointer-events", "none");
      setImportant(node, "opacity", "0");
      return;
    }

    if (node.dataset.ixBxThemed !== "1") return;
    node.style.removeProperty("display");
    node.style.removeProperty("visibility");
    node.style.removeProperty("pointer-events");
    node.style.removeProperty("opacity");
    const cornerLeft =
      document.documentElement.dataset.ixBxLauncherCorner === "left" &&
      typeof window !== "undefined" &&
      window.matchMedia(
        `(max-width: ${BITRIX_LAUNCHER_LEFT_MAX_WIDTH_PX}px)`,
      ).matches;
    if (cornerLeft) {
      setImportant(node, "right", "auto");
      setImportant(node, "left", "0");
    } else {
      setImportant(node, "left", "auto");
      setImportant(node, "right", "0");
    }
    setImportant(node, "top", "auto");
    setImportant(node, "bottom", BITRIX_HELLO_POPUP_ABOVE_LAUNCHER);
  });

  root.querySelectorAll(".b24-widget-button-popup-triangle").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "none");
  });
}

export function resolveBitrixLivechatPalette(isDark: boolean): BitrixLivechatPalette {
  if (isDark) {
    return {
      head: "#12141a",
      headFg: "#f5f5f7",
      surface: "#12141a",
      surfaceMuted: "#1c1f28",
      text: "#f5f5f7",
      textMuted: "#a1a1aa",
      border: "#2a2f3a",
      shadow: "0 16px 48px rgb(0 0 0 / 45%)",
      launcherBg: `linear-gradient(145deg, ${BRANDBOOK.rose} 0%, #9f1239 100%)`,
      launcherShadow: "0 4px 14px rgb(0 0 0 / 0.32)",
    };
  }

  return {
    head: BRANDBOOK.white,
    headFg: BRANDBOOK.text,
    surface: BRANDBOOK.white,
    surfaceMuted: BRANDBOOK.mist,
    text: BRANDBOOK.text,
    textMuted: BRANDBOOK.textMuted,
    border: "#e5e5ea",
    shadow: "0 16px 48px rgb(6 8 11 / 14%)",
    launcherBg: `linear-gradient(145deg, ${BRANDBOOK.rose} 0%, ${BRANDBOOK.red} 100%)`,
    launcherShadow: "0 4px 14px rgb(15 23 42 / 0.22)",
  };
}

function setImportant(el: HTMLElement, prop: string, value: string): void {
  el.style.setProperty(prop, value, "important");
}

function applyLivechatContentStyles(
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  const textColorSelectors = [
    ".bx-livechat-help-title",
    ".bx-imopenlines-form-result-title",
    ".bx-messenger-content-item-text",
    ".bx-im-message-content",
    ".bx-im-message-text",
  ];
  for (const selector of textColorSelectors) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "color", palette.text);
    });
  }

  root
    .querySelectorAll(
      ".bx-livechat-user-desc, [class*='user-desc'], [class*='operator-desc']",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "color", palette.textMuted);
      setImportant(node, "font-weight", "400");
    });

  root.querySelectorAll(".bx-livechat-user-name").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "color", palette.text);
    setImportant(node, "font-weight", "500");
  });

  const mutedTextSelectors = [
    ".bx-livechat-help-subtitle",
    ".bx-messenger-content-item-date",
    ".bx-im-message-content-date",
    ".bx-imopenlines-message-dialog-number",
  ];
  for (const selector of mutedTextSelectors) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "color", palette.textMuted);
    });
  }

  const isDark = palette.surface === "#12141a";
  const selfBubbleBg = isDark ? "rgba(225, 29, 72, 0.22)" : "rgba(225, 29, 72, 0.12)";

  root
    .querySelectorAll(".bx-im-message-type-self .bx-im-message-content")
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background-color", selfBubbleBg);
      setImportant(node, "color", palette.text);
    });

  root
    .querySelectorAll(
      ".bx-im-message-type-other .bx-im-message-content, .bx-im-message-type-system .bx-im-message-content",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background-color", palette.surfaceMuted);
      setImportant(node, "color", palette.text);
    });

  for (const selector of [
    ".bx-mobilechat-body",
    ".bx-mobilechat-box",
    ".bx-im-dialog",
    ".bx-im-dialog-list",
  ]) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background-color", palette.surface);
      setImportant(node, "color", palette.text);
    });
  }

  const datePillBg = isDark ? "#1c1f28" : "#e5e5ea";
  const datePillColor = isDark ? palette.textMuted : "#52525b";

  root.querySelectorAll(".bx-im-dialog-group-date").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "inline-block");
    setImportant(node, "padding", "0.25rem 0.75rem");
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "font-size", "0.75rem");
    setImportant(node, "font-weight", "500");
    setImportant(node, "line-height", "1.25");
    setImportant(node, "background-color", datePillBg);
    setImportant(node, "color", datePillColor);
    setImportant(node, "border", "none");
  });

  root.querySelectorAll(".bx-livechat-wrapper").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const isDark = palette.surface === "#12141a";
    node.dataset.ixBxThemed = "1";
    node.dataset.ixBxTheme = isDark ? "dark" : "light";
    node.style.setProperty("--ix-bx-head", palette.head);
    node.style.setProperty("--ix-bx-head-fg", palette.headFg);
    node.style.setProperty("--ix-bx-text", palette.text);
    node.style.setProperty("--ix-bx-text-muted", palette.textMuted);
    node.style.setProperty("--ix-bx-surface", palette.surface);
    node.style.setProperty("--ix-bx-surface-muted", palette.surfaceMuted);
    node.style.setProperty("--ix-bx-border", palette.border);
  });

  root.querySelectorAll(".bx-livechat-textarea").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "flex", "0 0 auto");
    setImportant(node, "padding", "0.75rem");
    setImportant(node, "border-top", `1px solid ${palette.border}`);
    setImportant(node, "background", palette.surface);
    setImportant(node, "background-color", palette.surface);
  });

  root.querySelectorAll(".bx-im-textarea").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", palette.surfaceMuted);
    setImportant(node, "background-color", palette.surfaceMuted);
    setImportant(node, "border", `1px solid ${palette.border}`);
    setImportant(node, "border-radius", "0.75rem");
    setImportant(node, "padding", "0.5rem 0.625rem 0.375rem");
  });

  root.querySelectorAll(".bx-im-textarea-box").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "padding", "0");
    setImportant(node, "margin", "0");
    setImportant(node, "display", "flex");
    setImportant(node, "align-items", "center");
    setImportant(node, "min-height", "2.5rem");
    setImportant(node, "height", "auto");
  });

  root
    .querySelectorAll(
      ".bx-im-textarea textarea, .bx-im-textarea input, .bx-im-textarea [contenteditable='true'], .bx-im-textarea-input",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background", "transparent");
      setImportant(node, "background-color", "transparent");
      setImportant(node, "border", "none");
      setImportant(node, "padding", "0.375rem 0.25rem");
      setImportant(node, "margin", "0");
      setImportant(node, "line-height", "1.45");
      setImportant(node, "min-height", "1.5rem");
      setImportant(node, "height", "auto");
      setImportant(node, "resize", "none");
      setImportant(node, "color", palette.text);
      setImportant(node, "font-family", BRANDBOOK.fontFamily);
    });

  root.querySelectorAll(".bx-im-textarea-app-box").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "margin", "0.25rem 0 0");
    setImportant(node, "padding", "0 0.125rem");
    setImportant(node, "display", "flex");
    setImportant(node, "gap", "0.5rem");
    setImportant(node, "height", "auto");
    setImportant(node, "flex", "0 0 auto");
  });

  root.querySelectorAll(".bx-im-textarea-app-button").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const isDark = palette.surface === "#12141a";
    if (isDark) {
      setImportant(node, "opacity", "0.88");
      setImportant(node, "filter", "brightness(1.5)");
    } else {
      setImportant(node, "opacity", "0.8");
      setImportant(node, "filter", "brightness(0) saturate(100%)");
    }
  });

  applyLivechatSmilesPanel(palette, root);
}

const LIVECHAT_MESSAGE_SELECTOR =
  ".bx-livechat-dialog .bx-messenger-content-item, .bx-livechat-dialog .bx-im-message:not(.bx-imopenlines-form-wrapper), .bx-livechat-dialog .bx-imopenlines-message";

function livechatHasMessages(wrapper: HTMLElement): boolean {
  return Boolean(wrapper.querySelector(LIVECHAT_MESSAGE_SELECTOR));
}

function livechatHasMessageBody(wrapper: HTMLElement): boolean {
  return Boolean(wrapper.querySelector(".bx-livechat-body-with-message"));
}

function livechatChatStarted(wrapper: HTMLElement): boolean {
  return (
    livechatHasMessages(wrapper) ||
    livechatHasMessageBody(wrapper) ||
    wrapper.dataset.ixBxChatStarted === "1"
  );
}

function hideLivechatWelcomeBodyDuplicate(wrapper: HTMLElement): void {
  if (!livechatHasMessageBody(wrapper)) {
    wrapper
      .querySelectorAll(".bx-livechat-body.ix-bx-welcome-body-hidden")
      .forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        node.classList.remove("ix-bx-welcome-body-hidden");
      });
    return;
  }

  wrapper
    .querySelectorAll(
      ".bx-livechat-box > .bx-livechat-body:not(.bx-livechat-body-with-message)",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.classList.add("ix-bx-welcome-body-hidden");
      setImportant(node, "display", "none");
      setImportant(node, "flex", "0 0 0");
      setImportant(node, "min-height", "0");
      setImportant(node, "height", "0");
      setImportant(node, "max-height", "0");
      setImportant(node, "padding", "0");
      setImportant(node, "margin", "0");
      setImportant(node, "overflow", "hidden");
      setImportant(node, "pointer-events", "none");
    });
}

/** До первого сообщения — welcome с операторами; после — диалог переписки. */
export function syncLivechatStartedView(root: ParentNode = document): void {
  root.querySelectorAll(".bx-livechat-wrapper").forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;

    const chatStarted = livechatChatStarted(wrapper);
    const messageBody = wrapper.querySelector(".bx-livechat-body-with-message");
    const dialog = wrapper.querySelector(
      ".bx-livechat-body-with-message .bx-livechat-dialog, .bx-livechat-dialog",
    );

    hideLivechatWelcomeBodyDuplicate(wrapper);

    wrapper.querySelectorAll(".bx-livechat-box > .bx-livechat-body").forEach((body) => {
      if (!(body instanceof HTMLElement)) return;
      if (body.classList.contains("ix-bx-welcome-body-hidden")) return;

      setImportant(body, "display", "flex");
      setImportant(body, "flex-direction", "column");
      setImportant(body, "flex", "1 1 auto");
      setImportant(body, "min-height", "0");
      setImportant(body, "overflow", "hidden");
      setImportant(body, "width", "100%");
      setImportant(body, "box-sizing", "border-box");

      const isMessagePane =
        body.classList.contains("bx-livechat-body-with-message") || chatStarted;

      if (isMessagePane) {
        setImportant(body, "align-items", "stretch");
        setImportant(body, "justify-content", "flex-start");
      } else {
        setImportant(body, "align-items", "center");
        setImportant(body, "justify-content", "center");
      }
    });

    if (!chatStarted) {
      wrapper.querySelectorAll(".bx-livechat-help-container").forEach((help) => {
        if (!(help instanceof HTMLElement)) return;
        if (help.classList.contains("bx-livechat-hide")) return;
        setImportant(help, "display", "flex");
        setImportant(help, "flex-direction", "column");
      });

      if (dialog instanceof HTMLElement) {
        setImportant(dialog, "display", "none");
      }
      return;
    }

    wrapper.querySelectorAll(".bx-livechat-help-container").forEach((help) => {
      if (!(help instanceof HTMLElement)) return;
      setImportant(help, "display", "none");
    });

    if (dialog instanceof HTMLElement) {
      setImportant(dialog, "display", "flex");
      setImportant(dialog, "flex-direction", "column");
      setImportant(dialog, "flex", "1 1 auto");
      setImportant(dialog, "min-height", "0");
      setImportant(dialog, "min-width", "0");
      setImportant(dialog, "width", "100%");
      setImportant(dialog, "max-width", "100%");
      setImportant(dialog, "align-self", "stretch");
      setImportant(dialog, "overflow", "hidden");
      setImportant(dialog, "justify-content", "flex-start");
    }

    if (messageBody instanceof HTMLElement) {
      setImportant(messageBody, "justify-content", "flex-start");
      setImportant(messageBody, "flex", "1 1 auto");
    }

    const messengerRoot = dialog ?? messageBody;
    if (!messengerRoot) return;

    messengerRoot
      .querySelectorAll(
        ".bx-mobilechat-body, .bx-mobilechat-box, .bx-messenger-box, .bx-messenger-content, .bx-im-dialog",
      )
      .forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        setImportant(node, "display", "flex");
        setImportant(node, "flex-direction", "column");
        setImportant(node, "flex", "1 1 auto");
        setImportant(node, "min-height", "0");
        setImportant(node, "min-width", "0");
        setImportant(node, "width", "100%");
        setImportant(node, "max-width", "100%");
        if (node.matches(".bx-mobilechat-body, .bx-messenger-content")) {
          setImportant(node, "justify-content", "flex-start");
        }
      });

    messengerRoot.querySelectorAll(".bx-im-dialog-list, .bx-messenger-content").forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "flex", "1 1 auto");
      setImportant(node, "min-height", "0");
      setImportant(node, "width", "100%");
      setImportant(node, "max-width", "100%");
      setImportant(node, "overflow", "auto");
      setImportant(node, "overflow-x", "hidden");
    });
  });
}

/** Пользователь отправил сообщение — переключить UI даже до появления пузыря в DOM. */
export function markLivechatSendStarted(root: ParentNode = document): void {
  root.querySelectorAll(".bx-livechat-wrapper").forEach((wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.dataset.ixBxChatStarted = "1";
  });
  syncLivechatStartedView(root);
}

function isEmojiSmilesSetSelected(root: ParentNode): boolean {
  const selected = root.querySelector(".bx-ui-smiles-set.bx-ui-smiles-set-selected");
  return Boolean(selected?.querySelector(".bx-ui-smiles-set-emoji-mac"));
}

function isBitrixSmilesPanelOpen(root: ParentNode): boolean {
  return Boolean(root.querySelector(".bx-livechat-alert-smiles-box .bx-ui-smiles-box"));
}

/** Переключить на emoji-набор (не битриксовские PNG). Возвращает true, если активен emoji. */
export function activateBitrixEmojiSmilesSet(root: ParentNode = document): boolean {
  if (!isBitrixSmilesPanelOpen(root)) return false;
  if (isEmojiSmilesSetSelected(root)) return true;

  const emojiIcon = root.querySelector(".bx-ui-smiles-set-emoji-mac");
  if (!(emojiIcon instanceof HTMLElement)) return false;

  const tab = emojiIcon.closest(".bx-ui-smiles-set");
  if (tab instanceof HTMLElement) {
    tab.dispatchEvent(
      new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window }),
    );
    tab.dispatchEvent(
      new MouseEvent("mouseup", { bubbles: true, cancelable: true, view: window }),
    );
    tab.click();
  }

  emojiIcon.dispatchEvent(
    new MouseEvent("mousedown", { bubbles: true, cancelable: true, view: window }),
  );
  emojiIcon.dispatchEvent(
    new MouseEvent("mouseup", { bubbles: true, cancelable: true, view: window }),
  );
  emojiIcon.click();

  return isEmojiSmilesSetSelected(root);
}

const emojiSmilesRetryTimers = new WeakMap<ParentNode, number>();

/** Повторяет переключение, пока Bitrix не отрисует панель и не выберет emoji. */
export function ensureBitrixEmojiSmilesSet(root: ParentNode = document): void {
  if (!isBitrixSmilesPanelOpen(root)) return;

  const prev = emojiSmilesRetryTimers.get(root);
  if (prev !== undefined) window.clearTimeout(prev);

  const attempt = (tryNo: number) => {
    if (!isBitrixSmilesPanelOpen(root)) return;
    if (activateBitrixEmojiSmilesSet(root)) return;
    if (tryNo >= 12) return;
    const timer = window.setTimeout(() => attempt(tryNo + 1), 50 * (tryNo + 1));
    emojiSmilesRetryTimers.set(root, timer);
  };

  attempt(0);
}

function applyLivechatSmilesPanel(
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  const isDark = palette.surface === "#12141a";

  if (isBitrixSmilesPanelOpen(root)) {
    ensureBitrixEmojiSmilesSet(root);
  }

  for (const selector of [
    ".bx-livechat-alert-box",
    ".bx-livechat-alert-smiles-box",
    ".bx-ui-smiles-box",
    ".bx-ui-smiles-elements-wrap",
    ".bx-ui-smiles-sets",
  ]) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background", isDark ? palette.surfaceMuted : palette.surface);
      setImportant(node, "background-color", isDark ? palette.surfaceMuted : palette.surface);
      setImportant(node, "border-color", palette.border);
      setImportant(node, "color", palette.text);
    });
  }

  // Один emoji-набор — табы и заголовок категории не нужны
  for (const selector of [".bx-ui-smiles-sets", ".bx-ui-smiles-category"]) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "display", "none");
    });
  }

  // Скрываем вкладку битриксовского PNG-набора (на случай если табы всплывут)
  root.querySelectorAll(".bx-ui-smiles-set").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const isBitrixPngTab =
      Boolean(node.querySelector("img.bx-ui-smiles-set-icon")) &&
      !node.querySelector(".bx-ui-smiles-set-emoji-mac");
    if (isBitrixPngTab) {
      setImportant(node, "display", "none");
    }
  });
}

function applyLivechatRateFormStyles(
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  const isDark = palette.surface === "#12141a";
  const likeIcon = isDark ? BITRIX_RATE_LIKE_ICON_DARK : BITRIX_RATE_LIKE_ICON_LIGHT;
  const dislikeIcon = isDark
    ? BITRIX_RATE_DISLIKE_ICON_DARK
    : BITRIX_RATE_DISLIKE_ICON_LIGHT;

  root.querySelectorAll(".bx-livechat-btn-like").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const active = isBitrixLikeControlActive(node);
    setImportant(node, "background-color", active ? BRANDBOOK.rose : palette.surfaceMuted);
    setImportant(
      node,
      "background-image",
      active ? BITRIX_RATE_LIKE_ICON_ACTIVE : likeIcon,
    );
    setImportant(node, "background-repeat", "no-repeat");
    setImportant(node, "background-position", "center");
    setImportant(node, "background-size", "1.5rem 1.5rem");
    setImportant(node, "border", active ? "none" : `1px solid ${palette.border}`);
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "box-shadow", "none");
    setImportant(node, "width", "3rem");
    setImportant(node, "height", "3rem");
    setImportant(node, "opacity", "1");
    setImportant(node, "filter", "none");
  });

  root.querySelectorAll(".bx-livechat-btn-dislike").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background-color", palette.surfaceMuted);
    setImportant(node, "background-image", dislikeIcon);
    setImportant(node, "background-repeat", "no-repeat");
    setImportant(node, "background-position", "center");
    setImportant(node, "background-size", "1.5rem 1.5rem");
    setImportant(node, "border", `1px solid ${palette.border}`);
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "box-shadow", "none");
    setImportant(node, "width", "3rem");
    setImportant(node, "height", "3rem");
    setImportant(node, "opacity", "1");
    setImportant(node, "filter", "none");
  });

  root.querySelectorAll(".bx-im-message-like").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "inline-flex");
    setImportant(node, "align-items", "center");
    setImportant(node, "justify-content", "center");
    setImportant(node, "min-width", "1.375rem");
    setImportant(node, "min-height", "1.375rem");
    setImportant(node, "padding", "0.125rem");
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "background-color", palette.surface);
    setImportant(node, "border", `1px solid ${palette.border}`);
    setImportant(node, "box-shadow", "0 1px 3px rgb(0 0 0 / 0.12)");
    setImportant(node, "opacity", "1");
    setImportant(node, "visibility", "visible");
    setImportant(node, "filter", "none");
    setImportant(node, "background-image", likeIcon);
    setImportant(node, "background-repeat", "no-repeat");
    setImportant(node, "background-position", "center");
    setImportant(node, "background-size", "0.875rem 0.875rem");

    node.querySelectorAll("svg, img").forEach((icon) => {
      if (!(icon instanceof HTMLElement)) return;
      setImportant(icon, "display", "block");
      setImportant(icon, "opacity", "1");
      setImportant(icon, "visibility", "visible");
      if (isDark) {
        setImportant(icon, "filter", "brightness(0) invert(1)");
      } else {
        setImportant(icon, "filter", "brightness(0) saturate(100%)");
      }
    });
  });

  root.querySelectorAll(".bx-livechat-control-btn").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;

    if (isBitrixLikeControlButton(node)) {
      const active = isBitrixLikeControlActive(node);
      setImportant(node, "filter", "none");
      setImportant(node, "opacity", "1");
      setImportant(node, "background-repeat", "no-repeat");
      setImportant(node, "background-position", "center");
      setImportant(node, "background-size", "1.125rem 1.125rem");
      setImportant(node, "border-radius", "9999px");
      setImportant(node, "background-color", active ? BRANDBOOK.rose : palette.surfaceMuted);
      setImportant(
        node,
        "background-image",
        active ? BITRIX_RATE_LIKE_ICON_ACTIVE : likeIcon,
      );
      setImportant(node, "border", active ? "none" : `1px solid ${palette.border}`);
      return;
    }

    if (
      !node.classList.contains("bx-livechat-control-btn-close") &&
      !node.classList.contains("bx-livechat-control-btn-hide")
    ) {
      return;
    }

    setImportant(node, "opacity", "0.72");
    if (isDark) {
      setImportant(node, "filter", "brightness(0) invert(1)");
    } else {
      setImportant(node, "filter", "brightness(0)");
    }
  });

  root.querySelectorAll(".bx-livechat-alert-close").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "opacity", "0.72");
    if (isDark) {
      setImportant(node, "filter", "brightness(0) invert(1)");
    } else {
      setImportant(node, "filter", "brightness(0)");
    }
  });

  root.querySelectorAll(".bx-livechat-alert-title").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "color", palette.text);
  });

  root.querySelectorAll(".bx-livechat-alert-later-btn").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "color", palette.textMuted);
  });
}

function applyLivechatCrmFormStyles(
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  const formBg = palette.surfaceMuted;
  const inputBg = palette.surface;

  for (const selector of [
    ".bx-imopenlines-form-wrapper",
    ".bx-imopenlines-form-content",
    ".b24-form",
    ".b24-form-wrapper",
    ".b24-form-header",
    ".b24-form-content",
    ".b24-form-state",
    ".b24-form-loader",
    ".b24-form-warning",
    ".b24-window-popup-wrapper",
  ]) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background-color", formBg);
      setImportant(node, "color", palette.text);
      setImportant(node, "border-color", palette.border);
      setImportant(node, "max-width", "100%");
      setImportant(node, "box-sizing", "border-box");
    });
  }

  root
    .querySelectorAll(
      ".b24-form-header-description, .b24-form-control-label, .b24-form-control-desc, .b24-form-field-agreement-link, .b24-form-control-required",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "color", palette.text);
    });

  root
    .querySelectorAll(
      ".b24-form-control-container input, .b24-form-control-container textarea, .b24-form-control input, .b24-form-control textarea",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background-color", inputBg);
      setImportant(node, "color", palette.text);
      setImportant(node, "border", `1px solid ${palette.border}`);
      setImportant(node, "border-radius", "0.5rem");
    });

  root.querySelectorAll(".b24-form-btn, button.b24-form-btn").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background-color", BRANDBOOK.rose);
    setImportant(node, "color", BRANDBOOK.white);
    setImportant(node, "border", "none");
    setImportant(node, "border-radius", "0.5rem");
  });

  root.querySelectorAll(".bx-imopenlines-form-wrapper").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    const isDark = palette.surface === "#12141a";
    setImportant(node, "padding", "0");
    setImportant(node, "max-width", "100%");
    setImportant(node, "width", "100%");
    setImportant(node, "background-color", formBg);
    if (isDark) {
      setImportant(node, "margin", "0");
    } else {
      setImportant(node, "margin", "0 0.75rem");
      setImportant(node, "width", "auto");
      setImportant(node, "max-width", "calc(100% - 1.5rem)");
    }
  });

  root.querySelectorAll(".b24-form-wrapper.b24-form-border-bottom").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "border-bottom", `1px solid ${palette.border}`);
    setImportant(node, "border-left", `1px solid ${palette.border}`);
    setImportant(node, "border-right", `1px solid ${palette.border}`);
    setImportant(node, "border-top", `1px solid ${palette.border}`);
    setImportant(node, "box-shadow", "none");
  });

  root.querySelectorAll(".b24-form-padding-side").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "padding-left", "0.75rem");
    setImportant(node, "padding-right", "0.75rem");
  });

  root.querySelectorAll(".b24-form-sign").forEach((node) => {
    node.remove();
  });
}

function applyLivechatChromeStyles(
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  root.querySelectorAll(".bx-im-textarea-send-button").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", bitrixSendButtonBackground(BRANDBOOK.rose));
    setImportant(node, "background-color", BRANDBOOK.rose);
    setImportant(node, "background-image", BITRIX_SEND_ARROW_ICON);
    setImportant(node, "background-repeat", "no-repeat");
    setImportant(node, "background-position", "center");
    setImportant(node, "background-size", "14px 14px");
    setImportant(node, "border", "none");
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "box-shadow", "none");
    setImportant(node, "opacity", "1");
  });

  const isDark = palette.surface === "#12141a";
  root
    .querySelectorAll(
      ".bx-livechat-control-btn-close, .bx-livechat-control-btn-hide, .bx-livechat-head .bx-livechat-icon",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (isBitrixLikeControlButton(node)) return;
      if (isDark) {
        setImportant(node, "filter", "brightness(0) invert(1)");
        setImportant(node, "opacity", "0.9");
      } else {
        setImportant(node, "filter", "brightness(0)");
        setImportant(node, "opacity", "0.72");
      }
    });
}

/** Только кнопка отправки — Bitrix перебивает inline background-color. */
export function syncBitrixSendButtonStyle(root: ParentNode = document): void {
  root.querySelectorAll(".bx-im-textarea-send-button").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", bitrixSendButtonBackground(BRANDBOOK.rose));
    setImportant(node, "background-color", BRANDBOOK.rose);
    setImportant(node, "background-image", BITRIX_SEND_ARROW_ICON);
    setImportant(node, "background-repeat", "no-repeat");
    setImportant(node, "background-position", "center");
    setImportant(node, "background-size", "14px 14px");
  });
}

function applyBitrixLivechatHeaderLogo(
  logo: BitrixLivechatHeaderLogo,
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  root.querySelectorAll(".bx-livechat-head-wrap").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", palette.head);
    setImportant(node, "background-color", palette.head);
    setImportant(node, "overflow", "visible");
  });

  root.querySelectorAll(".bx-livechat-head").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "flex");
    setImportant(node, "align-items", "center");
    setImportant(node, "gap", "0.5rem");
    setImportant(node, "padding", "0 1rem");
    setImportant(node, "min-height", "3.5rem");
    setImportant(node, "background", palette.head);
    setImportant(node, "background-color", palette.head);
    setImportant(node, "color", palette.headFg);
    setImportant(node, "border-bottom", `1px solid ${palette.border}`);
    setImportant(node, "overflow", "visible");
  });

  root.querySelectorAll(".bx-livechat-control-box").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "flex", "0 0 auto");
    setImportant(node, "margin-left", "auto");
  });

  root.querySelectorAll(".bx-livechat-title").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.classList.add("ix-bx-logo");
    node.dataset.ixBxLogoVariant = logo.mode;

    if (logo.mode === "lockup") {
      let lockup = node.querySelector<HTMLElement>(".ix-bx-logo-lockup");
      if (!lockup) {
        node.replaceChildren();
        lockup = document.createElement("div");
        lockup.className = "ix-bx-logo-lockup";

        const mark = document.createElement("img");
        mark.className = "ix-bx-logo-mark";
        mark.decoding = "async";
        mark.alt = "";

        const text = document.createElement("span");
        text.className = "ix-bx-logo-text";
        text.innerHTML = 'Иде<span class="ix-bx-logo-accent">А</span>литика';

        lockup.append(mark, text);
        node.appendChild(lockup);
      }

      const mark = lockup.querySelector<HTMLImageElement>("img.ix-bx-logo-mark");
      if (mark && !mark.src.endsWith(logo.markSrc)) {
        mark.src = logo.markSrc;
      }

      setImportant(node, "flex", "1 1 auto");
      setImportant(node, "min-width", "0");
      setImportant(node, "height", "auto");
      setImportant(node, "max-width", "none");
      setImportant(node, "overflow", "visible");
      setImportant(lockup, "display", "flex");
      setImportant(lockup, "align-items", "center");
      setImportant(lockup, "gap", "0.5rem");
      setImportant(lockup, "min-width", "0");
      setImportant(lockup, "overflow", "visible");

      if (mark) {
        setImportant(mark, "display", "block");
        setImportant(mark, "width", `${logo.height}px`);
        setImportant(mark, "height", `${logo.height}px`);
        setImportant(mark, "flex-shrink", "0");
      }
      return;
    }

    let img = node.querySelector<HTMLImageElement>("img.ix-bx-logo-img");
    if (!img) {
      node.replaceChildren();
      img = document.createElement("img");
      img.className = "ix-bx-logo-img";
      img.decoding = "async";
      node.appendChild(img);
    }

    if (!img.src.endsWith(logo.markSrc)) {
      img.src = logo.markSrc;
    }
    img.alt = logo.alt;

    setImportant(node, "flex", "0 0 auto");
    setImportant(node, "width", `${logo.height}px`);
    setImportant(node, "height", `${logo.height}px`);
    setImportant(node, "overflow", "visible");
    setImportant(img, "display", "block");
    setImportant(img, "width", `${logo.height}px`);
    setImportant(img, "height", `${logo.height}px`);
  });
}

/** Перебить inline-стили Bitrix (у них background с !important в style). */
export function applyBitrixWidgetBranding(
  palette: BitrixLivechatPalette,
  logo: BitrixLivechatHeaderLogo,
  root: ParentNode = document,
): void {
  applyBitrixLivechatHeaderLogo(logo, palette, root);

  root.querySelectorAll(".bx-livechat-head").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "font-family", BRANDBOOK.fontFamily);
    setImportant(node, "font-weight", "400");
  });

  root.querySelectorAll(".bx-livechat-box").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", palette.surface);
    setImportant(node, "background-color", palette.surface);
    setImportant(node, "border-color", palette.border);
    setImportant(node, "border-radius", "0.75rem");
    setImportant(node, "box-shadow", palette.shadow);
  });

  root.querySelectorAll(".bx-livechat-body").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", palette.surface);
    setImportant(node, "background-color", palette.surface);
    setImportant(node, "color", palette.text);
  });

  root.querySelectorAll(".bx-livechat-dialog").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", palette.surface);
    setImportant(node, "background-color", palette.surface);
    setImportant(node, "color", palette.text);
  });

  root.querySelectorAll(".bx-livechat-copyright").forEach((node) => {
    node.remove();
  });

  applyLivechatContentStyles(palette, root);
  applyLivechatChromeStyles(palette, root);
  applyLivechatRateFormStyles(palette, root);
  applyLivechatCrmFormStyles(palette, root);
  syncLivechatStartedView(root);

  root.querySelectorAll(".b24-widget-button-inner-block").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", palette.launcherBg);
    setImportant(node, "background-color", palette.head);
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "border", "none");
    setImportant(node, "display", "flex");
    setImportant(node, "align-items", "center");
    setImportant(node, "justify-content", "center");
  });

  root.querySelectorAll(".b24-widget-button-block").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "background", "transparent");
    setImportant(node, "background-color", "transparent");
    setImportant(node, "box-shadow", "none");
    setImportant(node, "border", "none");
    setImportant(node, "width", "3.5rem");
    setImportant(node, "height", "3.5rem");
  });

  root.querySelectorAll(".b24-widget-button-inner-container").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "width", "3.5rem");
    setImportant(node, "height", "3.5rem");
  });

  root.querySelectorAll(".b24-widget-button-inner-mask").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "none");
  });

  root.querySelectorAll(".b24-widget-button-icon-container").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "position", "static");
    setImportant(node, "display", "flex");
    setImportant(node, "align-items", "center");
    setImportant(node, "justify-content", "center");
    setImportant(node, "width", "100%");
    setImportant(node, "height", "100%");
    setImportant(node, "background", `url("${BITRIX_LIVECHAT_LAUNCHER_ICON}") center / 1.75rem no-repeat`);
  });

  root
    .querySelectorAll(
      ".b24-widget-button-icon-animation, .b24-widget-button-inner-item, .b24-widget-button-icon-container svg, .b24-widget-button-icon-container img",
    )
    .forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "display", "none");
      setImportant(node, "visibility", "hidden");
      setImportant(node, "opacity", "0");
      setImportant(node, "animation", "none");
      setImportant(node, "transform", "none");
    });

  root.querySelectorAll(".b24-widget-button-pulse").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "none");
  });

  for (const selector of [
    ".b24-widget-button-wrapper .bx-livechat-btn",
    ".b24-widget-button-wrapper .bx-livechat-button",
  ]) {
    root.querySelectorAll(selector).forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      setImportant(node, "background", palette.launcherBg);
      setImportant(node, "background-color", palette.head);
      setImportant(node, "box-shadow", palette.launcherShadow);
      setImportant(node, "border-radius", "9999px");
      setImportant(node, "border", "none");
    });
  }

  applyBitrixHelloPopupStyles(palette, root);
  syncBitrixCookieBannerLauncherOffset(root);
}

function applyBitrixHelloPopupStyles(
  palette: BitrixLivechatPalette,
  root: ParentNode,
): void {
  const isDark = palette.surface === "#12141a";
  const glass = resolveBitrixHelloPopupGlass(isDark);

  root.querySelectorAll(".b24-widget-button-popup").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    node.dataset.ixBxThemed = "1";
    node.dataset.ixBxTheme = isDark ? "dark" : "light";
    node.classList.add("ix-bx-hello-popup");
    setImportant(node, "background-color", glass.background);
    setImportant(node, "background", glass.background);
    setImportant(node, "-webkit-backdrop-filter", glass.blur);
    setImportant(node, "backdrop-filter", glass.blur);
    setImportant(node, "border", `1px solid ${glass.border}`);
    setImportant(node, "border-radius", "1rem");
    setImportant(node, "box-shadow", glass.shadow);
    setImportant(node, "color", palette.text);
    setImportant(node, "overflow", "hidden");
    setImportant(node, "max-width", "16.5rem");
    setImportant(node, "width", "min(16.5rem, calc(100vw - 2rem))");
    setImportant(node, "left", "auto");
    setImportant(node, "right", "0");
    setImportant(node, "top", "auto");
    setImportant(node, "bottom", BITRIX_HELLO_POPUP_ABOVE_LAUNCHER);
    setImportant(node, "cursor", "pointer");
    setImportant(node, "font-family", BRANDBOOK.fontFamily);
    setImportant(node, "isolation", "isolate");
  });

  root.querySelectorAll(".b24-widget-button-popup-inner").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "flex");
    setImportant(node, "align-items", "flex-start");
    setImportant(node, "gap", "0.75rem");
    setImportant(node, "padding", "0.75rem 2rem 0.75rem 0.75rem");
    setImportant(node, "background", "transparent");
    setImportant(node, "background-color", "transparent");
  });

  root.querySelectorAll(".b24-widget-button-popup-content").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "min-width", "0");
    setImportant(node, "flex", "1 1 auto");
  });

  root.querySelectorAll(".b24-widget-button-popup-image").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "position", "relative");
    setImportant(node, "flex-shrink", "0");
    setImportant(node, "margin", "0");
    setImportant(node, "width", "2.5rem");
    setImportant(node, "height", "2.5rem");
  });

  root.querySelectorAll(".b24-widget-button-popup-image-item").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "width", "2.5rem");
    setImportant(node, "height", "2.5rem");
    setImportant(node, "border-radius", "9999px");
    setImportant(node, "border", `1px solid ${palette.border}`);
    setImportant(node, "box-shadow", "none");
    setImportant(node, "background-size", "cover");
    setImportant(node, "background-position", "center");
  });

  root.querySelectorAll(".b24-widget-button-popup-name").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "color", palette.text);
    setImportant(node, "font-weight", "600");
    setImportant(node, "font-size", "0.875rem");
    setImportant(node, "line-height", "1.3");
    setImportant(node, "letter-spacing", "-0.01em");

    node.querySelectorAll("*").forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      setImportant(child, "color", palette.textMuted);
      setImportant(child, "font-weight", "400");
      setImportant(child, "font-size", "0.75rem");
      setImportant(child, "line-height", "1.35");
      setImportant(child, "display", "block");
      setImportant(child, "margin-top", "0.125rem");
    });
  });

  root.querySelectorAll(".b24-widget-button-popup-description").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "color", palette.textMuted);
    setImportant(node, "font-size", "0.8125rem");
    setImportant(node, "line-height", "1.45");
    setImportant(node, "margin-top", "0.25rem");

    node.querySelectorAll("*").forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      setImportant(child, "color", palette.textMuted);
    });
  });

  root.querySelectorAll(".b24-widget-button-popup-btn-hide").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (!node.getAttribute("aria-label")) {
      node.setAttribute("aria-label", "Закрыть");
    }
    node.setAttribute("role", "button");
    setImportant(node, "position", "absolute");
    setImportant(node, "top", "0.375rem");
    setImportant(node, "right", "0.375rem");
    setImportant(node, "width", "1.5rem");
    setImportant(node, "height", "1.5rem");
    setImportant(node, "display", "inline-flex");
    setImportant(node, "align-items", "center");
    setImportant(node, "justify-content", "center");
    setImportant(node, "border-radius", "0.375rem");
    setImportant(node, "background-color", "transparent");
    setImportant(
      node,
      "background-image",
      isDark ? BITRIX_HELLO_CLOSE_ICON_DARK : BITRIX_HELLO_CLOSE_ICON_LIGHT,
    );
    setImportant(node, "background-repeat", "no-repeat");
    setImportant(node, "background-position", "center");
    setImportant(node, "background-size", "14px 14px");
    setImportant(node, "border", "none");
    setImportant(node, "filter", "none");
    setImportant(node, "opacity", "0.65");
    setImportant(node, "cursor", "pointer");
    setImportant(node, "z-index", "2");

    node.querySelectorAll("img, svg").forEach((icon) => {
      if (!(icon instanceof HTMLElement)) return;
      setImportant(icon, "display", "none");
    });
  });

  root.querySelectorAll(".b24-widget-button-popup-triangle").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    setImportant(node, "display", "none");
  });
}

/** Перебить inline-стили лайков после клика (Bitrix добавляет active-классы). */
export function syncBitrixLikeButtonStyles(
  palette: BitrixLivechatPalette,
  root: ParentNode = document,
): void {
  applyLivechatRateFormStyles(palette, root);
}

/** CSS-переменные + запасные правила (если Bitrix не трогает inline). */
export const BITRIX_LIVECHAT_THEME_CSS = `
:root {
  --ix-bx-head: ${BRANDBOOK.white};
  --ix-bx-head-fg: ${BRANDBOOK.text};
  --ix-bx-surface: ${BRANDBOOK.white};
  --ix-bx-surface-muted: ${BRANDBOOK.mist};
  --ix-bx-text: ${BRANDBOOK.text};
  --ix-bx-text-muted: ${BRANDBOOK.textMuted};
  --ix-bx-border: #e5e5ea;
  --ix-bx-radius: 0.75rem;
  --ix-bx-shadow: 0 16px 48px rgb(6 8 11 / 14%);
}

html[data-theme="dark"] {
  --ix-bx-head: #12141a;
  --ix-bx-head-fg: #f5f5f7;
  --ix-bx-surface: #12141a;
  --ix-bx-surface-muted: #1c1f28;
  --ix-bx-text: #f5f5f7;
  --ix-bx-text-muted: #a1a1aa;
  --ix-bx-border: #2a2f3a;
  --ix-bx-shadow: 0 16px 48px rgb(0 0 0 / 45%);
}

@media (prefers-color-scheme: dark) {
  html:not([data-theme="light"]) {
    --ix-bx-head: #12141a;
    --ix-bx-head-fg: #f5f5f7;
    --ix-bx-surface: #12141a;
    --ix-bx-surface-muted: #1c1f28;
    --ix-bx-text: #f5f5f7;
    --ix-bx-text-muted: #a1a1aa;
    --ix-bx-border: #2a2f3a;
    --ix-bx-shadow: 0 16px 48px rgb(0 0 0 / 45%);
  }
}

.bx-livechat-wrapper,
.b24-widget-button-wrapper {
  font-family: ${BRANDBOOK.fontFamily} !important;
  z-index: 9000 !important;
}

:root {
  --ix-bx-launcher-bottom: 1.5rem;
  --ix-bx-launcher-inset: 1.5rem;
  --ix-cookie-banner-offset: 0px;
}

/* Форма/генерация: чат слева только на узком экране, где FAB может наехать на «Далее» */
@media (max-width: ${BITRIX_LAUNCHER_LEFT_MAX_WIDTH_PX}px) {
  html[data-ix-bx-launcher-corner="left"] .b24-widget-button-wrapper,
  html[data-ix-bx-launcher-corner="left"] .bx-livechat-wrapper {
    right: auto !important;
    left: var(--ix-bx-launcher-inset) !important;
  }

  html[data-ix-bx-launcher-corner="left"] .b24-widget-button-wrapper.b24-widget-button-position-bottom-right .b24-widget-button-popup,
  html[data-ix-bx-launcher-corner="left"] .b24-widget-button-position-bottom-right .b24-widget-button-popup[data-ix-bx-themed="1"],
  html[data-ix-bx-launcher-corner="left"] .b24-widget-button-popup[data-ix-bx-themed="1"] {
    right: auto !important;
    left: 0 !important;
  }
}

html[data-ix-cookie-banner="visible"] .b24-widget-button-wrapper,
html[data-ix-cookie-banner="visible"] .bx-livechat-wrapper {
  bottom: calc(var(--ix-bx-launcher-bottom) + var(--ix-cookie-banner-offset)) !important;
  transition: bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Hello-popup не показываем, пока не приняты cookies — иначе наезжает на баннер. */
html[data-ix-cookie-banner="visible"] .b24-widget-button-popup {
  display: none !important;
  visibility: hidden !important;
  pointer-events: none !important;
  opacity: 0 !important;
}

@media (prefers-reduced-motion: reduce) {
  html[data-ix-cookie-banner="visible"] .b24-widget-button-wrapper,
  html[data-ix-cookie-banner="visible"] .bx-livechat-wrapper {
    transition: none !important;
  }
}

.b24-widget-button-block,
.b24-widget-button-inner-container {
  width: 3.5rem !important;
  height: 3.5rem !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.b24-widget-button-inner-container.ix-bx-launcher-idle {
  position: relative !important;
}

.b24-widget-button-inner-container.ix-bx-launcher-idle::before {
  content: "" !important;
  position: absolute !important;
  inset: -2px !important;
  border-radius: 9999px !important;
  border: 2px solid rgb(225 29 72 / 0.38) !important;
  pointer-events: none !important;
  animation: ix-bx-launcher-ring 3.2s ease-out infinite !important;
}

.b24-widget-button-inner-mask {
  display: none !important;
}

.b24-widget-button-inner-block {
  position: relative !important;
  overflow: visible !important;
  width: 3.5rem !important;
  height: 3.5rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: none !important;
  box-shadow: 0 4px 14px rgb(15 23 42 / 0.22) !important;
}

.b24-widget-button-inner-container.ix-bx-launcher-idle .b24-widget-button-inner-block {
  animation:
    ix-bx-launcher-float 4s ease-in-out infinite,
    ix-bx-launcher-glow 4s ease-in-out infinite !important;
}

@keyframes ix-bx-launcher-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

@keyframes ix-bx-launcher-glow {
  0%, 100% { box-shadow: 0 4px 14px rgb(15 23 42 / 0.22); }
  50% { box-shadow: 0 8px 22px rgb(225 29 72 / 0.34); }
}

@keyframes ix-bx-launcher-ring {
  0% { transform: scale(1); opacity: 0.5; }
  75%, 100% { transform: scale(1.38); opacity: 0; }
}

html[data-theme="dark"] .b24-widget-button-inner-block {
  box-shadow: 0 4px 14px rgb(0 0 0 / 0.32) !important;
}

html[data-theme="dark"] .b24-widget-button-inner-container.ix-bx-launcher-idle .b24-widget-button-inner-block {
  animation:
    ix-bx-launcher-float 4s ease-in-out infinite,
    ix-bx-launcher-glow-dark 4s ease-in-out infinite !important;
}

html[data-theme="dark"] .b24-widget-button-inner-container.ix-bx-launcher-idle::before {
  border-color: rgb(251 113 133 / 0.42) !important;
}

@keyframes ix-bx-launcher-glow-dark {
  0%, 100% { box-shadow: 0 4px 14px rgb(0 0 0 / 0.32); }
  50% { box-shadow: 0 8px 22px rgb(225 29 72 / 0.38); }
}

@media (prefers-reduced-motion: reduce) {
  .b24-widget-button-inner-container.ix-bx-launcher-idle::before,
  .b24-widget-button-inner-container.ix-bx-launcher-idle .b24-widget-button-inner-block,
  .b24-widget-button-popup.b24-widget-button-popup-show[data-ix-bx-themed="1"],
  .b24-widget-button-popup.b24-widget-button-popup-show.ix-bx-hello-popup {
    animation: none !important;
  }
}

.b24-widget-button-pulse {
  display: none !important;
}

.b24-widget-button-icon-animation,
.b24-widget-button-icon-animation *,
.b24-widget-button-inner-item,
.b24-widget-button-icon-container svg,
.b24-widget-button-icon-container img {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  animation: none !important;
  transform: none !important;
  pointer-events: none !important;
}

.b24-widget-button-icon-container {
  position: static !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  background: url("${BITRIX_LIVECHAT_LAUNCHER_ICON}") center / 1.75rem no-repeat !important;
}

.b24-widget-button-wrapper:hover .b24-widget-button-inner-container.ix-bx-launcher-idle .b24-widget-button-inner-block,
.b24-widget-button-wrapper:hover .b24-widget-button-inner-block {
  animation: none !important;
  transform: scale(1.04) !important;
  box-shadow: 0 6px 18px rgb(15 23 42 / 0.28) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

html[data-theme="dark"] .b24-widget-button-wrapper:hover .b24-widget-button-inner-block {
  box-shadow: 0 6px 18px rgb(0 0 0 / 0.4) !important;
}

:root {
  --ix-bx-hello-glass-bg: rgba(255, 255, 255, 0.92);
  --ix-bx-hello-glass-border: rgba(6, 8, 11, 0.08);
  --ix-bx-hello-glass-shadow: 0 8px 24px rgb(6 8 11 / 0.1);
  --ix-bx-hello-close-icon: ${BITRIX_HELLO_CLOSE_ICON_LIGHT};
}

html[data-theme="dark"] {
  --ix-bx-hello-glass-bg: rgba(22, 24, 31, 0.9);
  --ix-bx-hello-glass-border: rgba(255, 255, 255, 0.08);
  --ix-bx-hello-glass-shadow: 0 8px 24px rgb(0 0 0 / 0.32);
  --ix-bx-hello-close-icon: ${BITRIX_HELLO_CLOSE_ICON_DARK};
}

@media (prefers-color-scheme: dark) {
  html:not([data-theme="light"]) {
    --ix-bx-hello-glass-bg: rgba(22, 24, 31, 0.9);
    --ix-bx-hello-glass-border: rgba(255, 255, 255, 0.08);
    --ix-bx-hello-glass-shadow: 0 8px 24px rgb(0 0 0 / 0.32);
    --ix-bx-hello-close-icon: ${BITRIX_HELLO_CLOSE_ICON_DARK};
  }
}

@keyframes ix-bx-hello-popup-in {
  from {
    opacity: 0;
    transform: translateY(6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* CRM hello-popup — компактная карточка над лаунчером */
.b24-widget-button-wrapper.b24-widget-button-position-bottom-right .b24-widget-button-popup,
.b24-widget-button-position-bottom-right .b24-widget-button-popup[data-ix-bx-themed="1"],
.b24-widget-button-popup.ix-bx-hello-popup {
  left: auto !important;
  right: 0 !important;
  top: auto !important;
  bottom: ${BITRIX_HELLO_POPUP_ABOVE_LAUNCHER} !important;
  width: min(16.5rem, calc(100vw - 2rem)) !important;
  max-width: 16.5rem !important;
  border-radius: 1rem !important;
  background: var(--ix-bx-hello-glass-bg) !important;
  -webkit-backdrop-filter: blur(12px) !important;
  backdrop-filter: blur(12px) !important;
  border: 1px solid var(--ix-bx-hello-glass-border) !important;
  box-shadow: var(--ix-bx-hello-glass-shadow) !important;
  color: var(--ix-bx-text) !important;
  overflow: hidden !important;
  font-family: ${BRANDBOOK.fontFamily} !important;
}

.b24-widget-button-popup.b24-widget-button-popup-show[data-ix-bx-themed="1"],
.b24-widget-button-popup.b24-widget-button-popup-show.ix-bx-hello-popup {
  animation: ix-bx-hello-popup-in 0.36s cubic-bezier(0.22, 1, 0.36, 1) both !important;
}

.b24-widget-button-popup-triangle,
.b24-widget-button-position-bottom-right .b24-widget-button-popup-triangle {
  display: none !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-inner,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-inner {
  display: flex !important;
  align-items: flex-start !important;
  gap: 0.75rem !important;
  padding: 0.75rem 2rem 0.75rem 0.75rem !important;
  background: transparent !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-content,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-content {
  min-width: 0 !important;
  flex: 1 1 auto !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-image,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-image {
  position: relative !important;
  flex-shrink: 0 !important;
  margin: 0 !important;
  width: 2.5rem !important;
  height: 2.5rem !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-image-item,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-image-item {
  width: 2.5rem !important;
  height: 2.5rem !important;
  border-radius: 9999px !important;
  border: 1px solid var(--ix-bx-border) !important;
  box-shadow: none !important;
  background-size: cover !important;
  background-position: center !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-name,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-name {
  color: var(--ix-bx-text) !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  line-height: 1.3 !important;
  letter-spacing: -0.01em !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-name *,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-name * {
  color: var(--ix-bx-text-muted) !important;
  font-weight: 400 !important;
  font-size: 0.75rem !important;
  line-height: 1.35 !important;
  display: block !important;
  margin-top: 0.125rem !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-description,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-description {
  color: var(--ix-bx-text-muted) !important;
  font-size: 0.8125rem !important;
  line-height: 1.45 !important;
  margin-top: 0.25rem !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-description *,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-description * {
  color: var(--ix-bx-text-muted) !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-btn-hide,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-btn-hide {
  position: absolute !important;
  top: 0.375rem !important;
  right: 0.375rem !important;
  width: 1.5rem !important;
  height: 1.5rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 0.375rem !important;
  background: transparent var(--ix-bx-hello-close-icon) center / 14px 14px no-repeat !important;
  border: none !important;
  filter: none !important;
  opacity: 0.65 !important;
  cursor: pointer !important;
  z-index: 2 !important;
  transition: opacity 0.15s ease !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-btn-hide img,
.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-btn-hide svg,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-btn-hide img,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-btn-hide svg {
  display: none !important;
}

.b24-widget-button-popup[data-ix-bx-themed="1"] .b24-widget-button-popup-btn-hide:hover,
.b24-widget-button-popup.ix-bx-hello-popup .b24-widget-button-popup-btn-hide:hover {
  opacity: 1 !important;
}

@media (prefers-reduced-motion: reduce) {
  .b24-widget-button-popup.b24-widget-button-popup-show[data-ix-bx-themed="1"],
  .b24-widget-button-popup.b24-widget-button-popup-show.ix-bx-hello-popup {
    animation: none !important;
  }
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn-close,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn-hide,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-head .bx-livechat-icon {
  opacity: 0.72 !important;
}

.bx-livechat-wrapper[data-ix-bx-theme="light"] .bx-livechat-control-btn-close,
.bx-livechat-wrapper[data-ix-bx-theme="light"] .bx-livechat-control-btn-hide,
.bx-livechat-wrapper[data-ix-bx-theme="light"] .bx-livechat-head .bx-livechat-icon {
  filter: brightness(0) !important;
  opacity: 0.72 !important;
}

.bx-livechat-wrapper[data-ix-bx-theme="light"] .bx-livechat-control-btn-close:hover,
.bx-livechat-wrapper[data-ix-bx-theme="light"] .bx-livechat-control-btn-hide:hover {
  opacity: 0.95 !important;
}

html[data-theme="dark"] .bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn-close,
html[data-theme="dark"] .bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn-hide,
html[data-theme="dark"] .bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-head .bx-livechat-icon,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-livechat-control-btn-close,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-livechat-control-btn-hide,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-livechat-head .bx-livechat-icon {
  filter: brightness(0) invert(1) !important;
  opacity: 0.9 !important;
}

@media (prefers-color-scheme: dark) {
  html:not([data-theme="light"]) .bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn-close,
  html:not([data-theme="light"]) .bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn-hide,
  html:not([data-theme="light"]) .bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-head .bx-livechat-icon {
    filter: brightness(0) invert(1) !important;
    opacity: 0.9 !important;
  }
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-send-button {
  background: ${BITRIX_SEND_ARROW_ICON} center / 14px 14px no-repeat, ${BRANDBOOK.rose} !important;
  background-color: ${BRANDBOOK.rose} !important;
  background-image: ${BITRIX_SEND_ARROW_ICON} !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 14px 14px !important;
  border: none !important;
  border-radius: 9999px !important;
  box-shadow: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-send-button:hover {
  background: ${BITRIX_SEND_ARROW_ICON} center / 14px 14px no-repeat, ${BRANDBOOK.red} !important;
  background-color: ${BRANDBOOK.red} !important;
  background-image: ${BITRIX_SEND_ARROW_ICON} !important;
}

/* Исходящие сообщения посетителя — rose вместо cyan Bitrix */
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-messenger-content-item-ol .bx-messenger-content-item-text,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-messenger-content-item-ol .bx-messenger-content-item-text-block,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-messenger-content-item-ol .bx-messenger-content-item-content {
  background-color: rgba(225, 29, 72, 0.18) !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-messenger-content-item-ol .bx-messenger-content-item-text,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-messenger-content-item-ol .bx-messenger-content-item-text-block,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-messenger-content-item-ol .bx-messenger-content-item-content {
  background-color: rgba(225, 29, 72, 0.12) !important;
  color: ${BRANDBOOK.ink} !important;
}

/* Форма «Представьтесь» внутри диалога (Bitrix CRM b24-form) */
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-wrapper,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-content,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-wrapper,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-header,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-content,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-state,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-loader,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-warning {
  background: var(--ix-bx-surface-muted) !important;
  background-color: var(--ix-bx-surface-muted) !important;
  color: var(--ix-bx-text) !important;
  border-color: var(--ix-bx-border) !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-wrapper {
  border-radius: 0.75rem !important;
  border: 1px solid var(--ix-bx-border) !important;
  border-bottom: 1px solid var(--ix-bx-border) !important;
  overflow: hidden !important;
  box-shadow: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-sign {
  display: none !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-wrapper {
  margin: 0 !important;
  padding: 0 !important;
  max-width: 100% !important;
  width: 100% !important;
  background: var(--ix-bx-surface-muted) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog-list-item .bx-imopenlines-form-wrapper {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-im-dialog-list-item .bx-imopenlines-form-wrapper {
  margin: 0 0.75rem !important;
  width: auto !important;
  max-width: calc(100% - 1.5rem) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog-group-date {
  display: inline-block !important;
  padding: 0.25rem 0.75rem !important;
  border-radius: 9999px !important;
  font-size: 0.75rem !important;
  font-weight: 500 !important;
  line-height: 1.25 !important;
  border: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-im-dialog-group-date {
  background-color: #e5e5ea !important;
  color: #52525b !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="dark"] .bx-im-dialog-group-date {
  background-color: #1c1f28 !important;
  color: #a1a1aa !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-message-dialog-number {
  color: var(--ix-bx-text-muted) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-imopenlines-message-dialog-number,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-im-message-content-date,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-messenger-content-item-date {
  color: #6b7280 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-padding-side {
  padding-left: 0.75rem !important;
  padding-right: 0.75rem !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-header-description,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-control-label,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-control-desc,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-field-agreement-link {
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-control-container input,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-control-container textarea,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-control input,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-control textarea {
  background: var(--ix-bx-surface) !important;
  border: 1px solid var(--ix-bx-border) !important;
  border-radius: 0.5rem !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .b24-form-btn {
  background: ${BRANDBOOK.rose} !important;
  color: ${BRANDBOOK.white} !important;
  border: none !important;
  border-radius: 0.5rem !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-wrap {
  background: var(--ix-bx-surface-muted) !important;
  border: 1px solid var(--ix-bx-border) !important;
  border-radius: 0.75rem !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-title,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-label {
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form input,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form textarea {
  background: var(--ix-bx-surface) !important;
  border: 1px solid var(--ix-bx-border) !important;
  border-radius: 0.5rem !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form input::placeholder,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form textarea::placeholder {
  color: var(--ix-bx-text-muted) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-head {
  display: flex !important;
  align-items: center !important;
  padding: 0 1rem !important;
  min-height: 3.5rem !important;
  background: var(--ix-bx-head) !important;
  color: var(--ix-bx-head-fg) !important;
  border-bottom: 1px solid var(--ix-bx-border) !important;
  overflow: visible !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-head-wrap {
  background: var(--ix-bx-head) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-title.ix-bx-logo {
  flex: 1 1 auto !important;
  min-width: 0 !important;
  overflow: visible !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .ix-bx-logo-lockup {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  min-width: 0 !important;
  overflow: visible !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .ix-bx-logo-mark {
  display: block !important;
  width: 28px !important;
  height: 28px !important;
  flex-shrink: 0 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .ix-bx-logo-text {
  font-family: ${BRANDBOOK.fontFamily} !important;
  font-size: 1.0625rem !important;
  font-weight: 600 !important;
  line-height: 1 !important;
  letter-spacing: -0.01em !important;
  color: var(--ix-bx-head-fg) !important;
  white-space: nowrap !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .ix-bx-logo-accent {
  color: ${BRANDBOOK.rose} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-title.ix-bx-logo[data-ix-bx-logo-variant="mark"] {
  flex: 0 0 auto !important;
  width: 28px !important;
  height: 28px !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-title.ix-bx-logo[data-ix-bx-logo-variant="mark"] .ix-bx-logo-img {
  display: block !important;
  width: 28px !important;
  height: 28px !important;
}

/* Смайлики: один emoji-набор — без табов и заголовка категории */
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-ui-smiles-sets,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-ui-smiles-category {
  display: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-ui-smiles-set:has(img.bx-ui-smiles-set-icon):not(:has(.bx-ui-smiles-set-emoji-mac)) {
  display: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-alert-box,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-alert-smiles-box,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-ui-smiles-box,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-ui-smiles-elements-wrap,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-ui-smiles-sets {
  background: var(--ix-bx-surface) !important;
  border-color: var(--ix-bx-border) !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-livechat-alert-box,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-livechat-alert-smiles-box,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-ui-smiles-box,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-ui-smiles-elements-wrap,
.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-ui-smiles-sets {
  background: var(--ix-bx-surface-muted) !important;
}

.bx-livechat-copyright {
  display: none !important;
}

/* --- Livechat content UI --- */
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-box {
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-body {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
  width: 100% !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-body:not(.bx-livechat-body-with-message) {
  align-items: center !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-body-with-message,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-chat-started="1"] .bx-livechat-body {
  align-items: stretch !important;
  justify-content: flex-start !important;
  flex: 1 1 auto !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-box:has(.bx-livechat-body-with-message) > .bx-livechat-body:not(.bx-livechat-body-with-message),
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-body.ix-bx-welcome-body-hidden {
  display: none !important;
  flex: 0 0 0 !important;
  height: 0 !important;
  min-height: 0 !important;
  max-height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
  pointer-events: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-dialog {
  display: none !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-mobilechat-wrapper,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-mobilechat-box,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog-list,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-wrap {
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-mobilechat-wrapper,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-mobilechat-box,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog-list {
  background: var(--ix-bx-surface) !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-message-type-self .bx-im-message-content {
  background-color: rgba(225, 29, 72, 0.22) !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-im-message-type-self .bx-im-message-content {
  background-color: rgba(225, 29, 72, 0.12) !important;
  color: ${BRANDBOOK.ink} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-message-type-other .bx-im-message-content,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-message-type-system .bx-im-message-content {
  background-color: var(--ix-bx-surface-muted) !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-alert-box.bx-livechat-form-rate-show {
  width: 100% !important;
  max-width: 100% !important;
  flex-shrink: 0 !important;
  box-sizing: border-box !important;
  border-top: 1px solid var(--ix-bx-border) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-btn-like,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-btn-dislike {
  width: 3rem !important;
  height: 3rem !important;
  border-radius: 9999px !important;
  border: 1px solid var(--ix-bx-border) !important;
  background-color: var(--ix-bx-surface-muted) !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 1.5rem 1.5rem !important;
  box-shadow: none !important;
  filter: none !important;
  opacity: 1 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-btn-like.bx-livechat-btn-like-active,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-btn-like-active {
  background-color: ${BRANDBOOK.rose} !important;
  background-image: ${BITRIX_RATE_LIKE_ICON_ACTIVE} !important;
  border-color: ${BRANDBOOK.rose} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="dark"] .bx-livechat-btn-like:not(.bx-livechat-btn-like-active) {
  background-image: ${BITRIX_RATE_LIKE_ICON_DARK} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-livechat-btn-like:not(.bx-livechat-btn-like-active) {
  background-image: ${BITRIX_RATE_LIKE_ICON_LIGHT} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="dark"] .bx-livechat-btn-dislike {
  background-image: ${BITRIX_RATE_DISLIKE_ICON_DARK} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-livechat-btn-dislike {
  background-image: ${BITRIX_RATE_DISLIKE_ICON_LIGHT} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-message-like {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 1.375rem !important;
  min-height: 1.375rem !important;
  padding: 0.125rem !important;
  border-radius: 9999px !important;
  background: var(--ix-bx-surface) !important;
  border: 1px solid var(--ix-bx-border) !important;
  box-shadow: 0 1px 3px rgb(0 0 0 / 0.12) !important;
  opacity: 1 !important;
  visibility: visible !important;
  filter: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="dark"] .bx-im-message-like {
  background-image: ${BITRIX_RATE_LIKE_ICON_DARK} !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 0.875rem 0.875rem !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-im-message-like {
  background-image: ${BITRIX_RATE_LIKE_ICON_LIGHT} !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 0.875rem 0.875rem !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn[class*="like"],
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn[class*="vote"],
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn[class*="rating"] {
  filter: none !important;
  opacity: 1 !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 1.125rem 1.125rem !important;
  border-radius: 9999px !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn.bx-livechat-btn-like-active,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn[class*="like"].bx-livechat-btn-like-active,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-control-btn[class*="like"].active {
  background-color: ${BRANDBOOK.rose} !important;
  background-image: ${BITRIX_RATE_LIKE_ICON_ACTIVE} !important;
  border: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-alert-close {
  opacity: 0.72 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="dark"] .bx-livechat-alert-close {
  filter: brightness(0) invert(1) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-theme="light"] .bx-livechat-alert-close {
  filter: brightness(0) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-chat-started="1"] .bx-livechat-dialog,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-dialog:has(.bx-messenger-content-item),
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-dialog:has(.bx-im-message) {
  display: flex !important;
  flex: 1 1 auto !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
  background: var(--ix-bx-surface) !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-chat-started="1"] .bx-livechat-help-container,
.bx-livechat-wrapper[data-ix-bx-themed="1"]:has(.bx-livechat-dialog .bx-messenger-content-item) .bx-livechat-help-container,
.bx-livechat-wrapper[data-ix-bx-themed="1"]:has(.bx-livechat-dialog .bx-im-message) .bx-livechat-help-container {
  display: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-help-container:not(.bx-livechat-hide) {
  display: flex !important;
  flex: 1 1 auto !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 1.5rem 1.25rem 1rem !important;
  text-align: center !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-dialog .bx-messenger-box,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-chat-started="1"] .bx-messenger-box {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  display: flex !important;
  flex-direction: column !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-dialog .bx-messenger-content,
.bx-livechat-wrapper[data-ix-bx-themed="1"][data-ix-bx-chat-started="1"] .bx-messenger-content,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-dialog-list {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: flex-start !important;
  overflow: auto !important;
  overflow-x: hidden !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-help-title {
  font-size: 1.375rem !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
  margin: 0 0 1rem !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-help-subtitle {
  display: block !important;
  font-size: 0.9375rem !important;
  font-weight: 500 !important;
  margin-top: 0.375rem !important;
  color: var(--ix-bx-text-muted) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-help-user {
  display: flex !important;
  justify-content: center !important;
  gap: 1.25rem !important;
  flex-wrap: wrap !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-user {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-user-icon {
  width: 3.5rem !important;
  height: 3.5rem !important;
  border-radius: 9999px !important;
  border: 2px solid var(--ix-bx-border) !important;
  background-size: cover !important;
  background-position: center !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-user-name {
  margin-top: 0.375rem !important;
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
  color: var(--ix-bx-text) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-user-desc {
  margin-top: 0.125rem !important;
  font-size: 0.75rem !important;
  font-weight: 400 !important;
  color: var(--ix-bx-text-muted) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-textarea {
  flex: 0 0 auto !important;
  padding: 0.75rem !important;
  border-top: 1px solid var(--ix-bx-border) !important;
  background: var(--ix-bx-surface) !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea {
  background: var(--ix-bx-surface-muted) !important;
  border: 1px solid var(--ix-bx-border) !important;
  border-radius: 0.75rem !important;
  padding: 0.5rem 0.625rem 0.375rem !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-box {
  padding: 0 !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  min-height: 2.5rem !important;
  height: auto !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-input {
  background: transparent !important;
  border: none !important;
  padding: 0.375rem 0.25rem !important;
  margin: 0 !important;
  line-height: 1.45 !important;
  min-height: 1.5rem !important;
  height: auto !important;
  resize: none !important;
  color: var(--ix-bx-text) !important;
  font-family: ${BRANDBOOK.fontFamily} !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-input::placeholder {
  color: var(--ix-bx-text-muted) !important;
  opacity: 1 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-app-box {
  margin: 0.25rem 0 0 !important;
  padding: 0 0.125rem !important;
  display: flex !important;
  gap: 0.5rem !important;
  height: auto !important;
  flex: 0 0 auto !important;
}

.bx-livechat-wrapper[data-ix-bx-theme="light"] .bx-im-textarea-app-button {
  opacity: 0.8 !important;
  filter: brightness(0) saturate(100%) !important;
  transition: opacity 0.15s ease !important;
}

.bx-livechat-wrapper[data-ix-bx-theme="dark"] .bx-im-textarea-app-button {
  opacity: 0.88 !important;
  filter: brightness(1.5) !important;
  transition: opacity 0.15s ease !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-im-textarea-app-button:hover {
  opacity: 1 !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-textarea-resize-handle,
.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-livechat-widget-resize-handle {
  display: none !important;
}

.bx-livechat-wrapper[data-ix-bx-themed="1"] .bx-imopenlines-form-result-title {
  color: var(--ix-bx-text) !important;
}

@media (max-width: 480px) {
  .bx-livechat-wrapper.bx-livechat-show .bx-livechat-box {
    width: calc(100vw - 1rem) !important;
    max-width: calc(100vw - 1rem) !important;
  }
}
`.trim();
